import { useState, useEffect } from 'react';
import Panel from '../Panel';
import { INote } from '~/types/campaigns';
import clip from 'text-clipper';
import { safeParseHtml } from '~/utils/safeParseHtml';
import { isCampaignRequestNote } from '~/utils/notes';
import NotesModal from '~/components/Common/NotesModal';
import ActivityLog from '~/components/Common/ActivityLog';
import { formatDateIfValid } from '~/utils';
import { ContactAttempt } from '~/services/contactAttempts';
import { useContactAttempts } from '~/hooks/useContactAttempts';
import ContactAttemptsLoadError from '~/components/Common/ContactAttemptsLoadError';
import {
    fetchCampaignActivityLog,
    updateCampaignRequestNote,
    deleteCampaignRequestNote,
    fetchCampaignRequestNoteEditHistory,
} from '~/services/campaign';
import AddNoteInlineForm, {
    ADD_MSG_TYPE,
} from '~/components/CampaignDetail/forms/AddNoteInlineForm';
import { FaPlus, FaUser } from 'react-icons/fa6';
import { FiEye, FiEdit2 } from 'react-icons/fi';
import Check from '~/components/Check/Check';
import EditNoteModal from '~/components/Common/EditNoteModal';
import EditContactAttemptModal from '~/components/Common/EditContactAttemptModal';
import EditRequestNotesModal from '~/components/Common/EditRequestNotesModal';

type TabType = 'notes' | 'activity';

interface ApplicationHistoryProps {
    notes: INote[];
    campaign: {
        id?: number;
        campaign_id?: number;
        name?: string;
        campaign_name?: string;
    };
    setNotes: React.Dispatch<React.SetStateAction<INote[]>>;
    campaignRequest?: {
        id: number;
        initial_first_name?: string;
        initial_last_name?: string;
        patient_first_name?: string;
        patient_middle_name?: string;
        patient_last_name?: string;
        created_date?: string;
    };
    campaignRequestNotes?: string | null;
    onRequestNotesUpdate?: (updatedNotes: string) => void;
    campaignApproval?: {
        approved_date?: string | null;
        request_approved_date?: string | null;
        ps_approved_date?: string | null;
        finance_approved_date?: string | null;
        ps_approver_person?: {
            id: number;
            first_name: string;
            last_name: string;
            email: string;
        } | null;
        finance_approver_person?: {
            id: number;
            first_name: string;
            last_name: string;
            email: string;
        } | null;
        comments?: string;
    } | null;
    activityLogRefreshKey?: number;
}

// History panel for Campaign Application Detail page
// Combines Contact Attempts and Campaign Message notes into a single chronological list
const ApplicationHistory = ({
    notes,
    campaign,
    setNotes,
    campaignRequest,
    campaignRequestNotes,
    onRequestNotesUpdate,
    campaignApproval,
    activityLogRefreshKey = 0,
}: ApplicationHistoryProps) => {
    const {
        contactAttempts,
        setContactAttempts,
        loadingAttempts,
        attemptsFailed,
        loadContactAttempts,
    } = useContactAttempts(campaignRequest?.id);
    const [notesModal, setNotesModal] = useState<{
        isOpen: boolean;
        note: INote | null;
    }>({ isOpen: false, note: null });
    const [editModal, setEditModal] = useState<{
        isOpen: boolean;
        note: INote | null;
    }>({ isOpen: false, note: null });
    const [editAttemptModal, setEditAttemptModal] = useState<{
        isOpen: boolean;
        attempt: ContactAttempt | null;
    }>({ isOpen: false, attempt: null });
    const [editRequestNotesModal, setEditRequestNotesModal] = useState(false);
    const [activeForm, setActiveForm] = useState<'todo' | 'note' | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>('notes');
    const [activityLogCount, setActivityLogCount] = useState<number>(0);

    // Get the campaign ID for activity log
    const campaignId = campaign?.id || campaign?.campaign_id;
    const campaignRequestId = campaignRequest?.id;

    // The history list mixes campaign messages with campaign request notes.
    // Each table numbers its rows independently, so a request note sent to the
    // messages endpoint would land on an unrelated Message.
    const selectedNoteIsCRNote = notesModal.note
        ? isCampaignRequestNote(notesModal.note)
        : false;
    const editNoteIsCRNote = editModal.note
        ? isCampaignRequestNote(editModal.note)
        : false;

    // Fetch activity log count (re-fetch when refreshKey changes)
    useEffect(() => {
        const loadActivityLogCount = async () => {
            if (!campaignId) return;
            try {
                const response = await fetchCampaignActivityLog(
                    campaignId,
                    'all',
                    1,
                    1,
                    true
                );
                setActivityLogCount(response.count || 0);
            } catch (error) {
                console.error('Error fetching activity log count:', error);
            }
        };

        loadActivityLogCount();
    }, [campaignId, activityLogRefreshKey]);

    // For application detail, "notes" should be Message objects for the same campaign id
    const justNotes = notes || [];

    const hasRequestNotes = !!(
        campaignRequestNotes && campaignRequestNotes.trim().length > 0
    );
    const hasApproval = !!(
        campaignApproval &&
        (campaignApproval.ps_approved_date ||
            campaignApproval.finance_approved_date ||
            campaignApproval.approved_date ||
            campaignApproval.request_approved_date)
    );
    const totalNotesCount =
        (justNotes?.length || 0) +
        (hasRequestNotes ? 1 : 0) +
        (hasApproval ? 1 : 0);
    const totalHistoryCount = (contactAttempts?.length || 0) + totalNotesCount;

    type HistoryItem = {
        id: string;
        date: Date;
        type: 'contact_attempt' | 'note' | 'request_note' | 'approval';
        data:
            | ContactAttempt
            | INote
            | { note: string; created_by: string; created_date: string }
            | {
                  approved_date?: string | null;
                  ps_approved_date?: string | null;
                  finance_approved_date?: string | null;
                  ps_approver_person?: {
                      first_name: string;
                      last_name: string;
                  } | null;
                  finance_approver_person?: {
                      first_name: string;
                      last_name: string;
                  } | null;
                  comments?: string;
              };
    };

    const unifiedHistory: HistoryItem[] = [];

    // Add contact attempts
    contactAttempts.forEach((attempt) => {
        unifiedHistory.push({
            id: `contact-${attempt.id}`,
            date: new Date(attempt.attempt_datetime),
            type: 'contact_attempt',
            data: attempt,
        });
    });

    // Add message notes (campaign messages)
    justNotes.forEach((note) => {
        unifiedHistory.push({
            id: `note-${note.id}`,
            date: new Date(note.created_date),
            type: 'note',
            data: note,
        });
    });

    // Add legacy campaign request notes entry if present
    if (hasRequestNotes && campaignRequest?.created_date) {
        const initialContactName =
            campaignRequest?.initial_first_name &&
            campaignRequest?.initial_last_name
                ? `${campaignRequest.initial_first_name} ${campaignRequest.initial_last_name}`
                : 'System';
        unifiedHistory.push({
            id: 'request-note',
            date: new Date(campaignRequest.created_date),
            type: 'request_note',
            data: {
                note: campaignRequestNotes || '',
                created_by: initialContactName,
                created_date: campaignRequest.created_date,
            },
        });
    }

    // Add campaign approval entry if present
    if (
        campaignApproval &&
        (campaignApproval.ps_approved_date ||
            campaignApproval.finance_approved_date ||
            campaignApproval.approved_date ||
            campaignApproval.request_approved_date)
    ) {
        // Use the earliest approval date available
        const approvalDate =
            campaignApproval.ps_approved_date ||
            campaignApproval.finance_approved_date ||
            campaignApproval.request_approved_date ||
            campaignApproval.approved_date;

        if (approvalDate) {
            unifiedHistory.push({
                id: 'approval',
                date: new Date(approvalDate),
                type: 'approval',
                data: campaignApproval,
            });
        }
    }

    // Sort by date (newest first)
    unifiedHistory.sort((a, b) => b.date.getTime() - a.date.getTime());

    return (
        <Panel addClasses='bg-white border-stroke !p-3'>
            <div className='p-1'>
                {/* Tab Navigation */}
                <div className='mb-5 flex rounded-full bg-[#FFF5E6] p-1'>
                    <button
                        onClick={() => setActiveTab('notes')}
                        className={`flex-1 rounded-full px-4 py-2 text-[14px]/[18px] font-bold transition-colors ${
                            activeTab === 'notes'
                                ? 'bg-[#FEBF3E] text-black'
                                : 'bg-transparent text-body hover:bg-[#FFE4B5]'
                        }`}
                    >
                        Campaign Notes{' '}
                        <span
                            className={`ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                                activeTab === 'notes'
                                    ? 'bg-black text-white'
                                    : 'bg-body text-white'
                            }`}
                        >
                            {totalHistoryCount}
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab('activity')}
                        className={`flex-1 rounded-full px-4 py-2 text-[14px]/[18px] font-bold transition-colors ${
                            activeTab === 'activity'
                                ? 'bg-[#FEBF3E] text-black'
                                : 'bg-transparent text-body hover:bg-[#FFE4B5]'
                        }`}
                    >
                        Activity Log{' '}
                        <span
                            className={`ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                                activeTab === 'activity'
                                    ? 'bg-black text-white'
                                    : 'bg-body text-white'
                            }`}
                        >
                            {activityLogCount}
                        </span>
                    </button>
                </div>

                {/* Notes Tab Content */}
                {activeTab === 'notes' && (
                    <>
                        <div className='mb-5 flex flex-wrap items-center justify-between gap-2'>
                            <h3 className='text-[22px]/[28px] font-bold'>
                                Campaign Notes ({totalHistoryCount})
                            </h3>
                            <div className='flex gap-2'>
                                <button
                                    onClick={() =>
                                        setActiveForm(
                                            activeForm === 'todo' ? null : 'todo'
                                        )
                                    }
                                    className='inline-flex shrink items-center justify-center gap-1 whitespace-nowrap rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[12px] py-[6px] text-[11px]/[14px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#E6A830] hover:bg-[#E6A830] sm:gap-2 sm:px-[20px] sm:py-[7px] sm:text-[13px]/[16px]'
                                >
                                    <FaPlus className='flex-shrink-0 text-[10px] sm:text-[12px]' />
                                    Add To do
                                </button>
                                <button
                                    onClick={() =>
                                        setActiveForm(
                                            activeForm === 'note' ? null : 'note'
                                        )
                                    }
                                    className='hover:bg-gray-100 inline-flex shrink items-center justify-center gap-1 whitespace-nowrap rounded-full border-[2px] border-black bg-white px-[12px] py-[6px] text-[11px]/[14px] font-bold uppercase text-black transition-colors duration-500 sm:gap-2 sm:px-[20px] sm:py-[7px] sm:text-[13px]/[16px]'
                                >
                                    <FaPlus className='flex-shrink-0 text-[10px] sm:text-[12px]' />
                                    Add Note
                                </button>
                            </div>
                        </div>

                        {activeForm === 'todo' && (
                            <AddNoteInlineForm
                                type={ADD_MSG_TYPE.ONLY_TODO}
                                campaign={campaign}
                                setNotes={setNotes}
                                onClose={() => setActiveForm(null)}
                            />
                        )}

                        {activeForm === 'note' && (
                            <AddNoteInlineForm
                                type={ADD_MSG_TYPE.ONLY_NOTE}
                                campaign={campaign}
                                setNotes={setNotes}
                                onClose={() => setActiveForm(null)}
                            />
                        )}

                        {attemptsFailed && !loadingAttempts && (
                            <ContactAttemptsLoadError onRetry={loadContactAttempts} />
                        )}

                        {loadingAttempts ? (
                            <div className='flex items-center justify-center py-8'>
                                <div className='text-gray-500'>
                                    Loading history...
                                </div>
                            </div>
                        ) : unifiedHistory.length === 0 && !attemptsFailed ? (
                            <div className='flex items-center justify-center py-8'>
                                <div className='text-gray-500'>
                                    No history found. Add the first entry!
                                </div>
                            </div>
                        ) : (
                            <div className='mt-3 space-y-3'>
                        {unifiedHistory.map((item) => {
                            if (item.type === 'contact_attempt') {
                                const attempt = item.data as ContactAttempt;
                                const date = new Date(attempt.attempt_datetime);
                                return (
                                    <div key={item.id}>
                                        <div className='flex items-center justify-between'>
                                            <div className='text-[12px]/[16px]'>
                                                {date.toLocaleDateString(
                                                    'en-US',
                                                    {
                                                        year: '2-digit',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                    }
                                                )}{' '}
                                                Contact Attempt
                                            </div>
                                            <div className='flex items-center gap-2'>
                                                <div className='text-[12px]/[16px] font-bold'>
                                                    {attempt.person_name ||
                                                        'Unknown'}
                                                </div>
                                                <button
                                                    onClick={() =>
                                                        setEditAttemptModal({
                                                            isOpen: true,
                                                            attempt,
                                                        })
                                                    }
                                                    title='Edit contact attempt'
                                                >
                                                    <FiEdit2 className='text-primary' />
                                                </button>
                                            </div>
                                        </div>
                                        <div className='bg-[#F4F4F5] p-2 text-[14px]/[20px]'>
                                            {attempt.note || (
                                                <em className='text-gray-500'>
                                                    No note provided
                                                </em>
                                            )}
                                        </div>
                                        <div className='text-right text-[12px]/[16px] text-[#949599]'>
                                            {attempt.method_display}
                                        </div>
                                    </div>
                                );
                            } else if (item.type === 'request_note') {
                                const requestNote = item.data as {
                                    note: string;
                                    created_by: string;
                                    created_date: string;
                                };
                                return (
                                    <div key={item.id}>
                                        <div className='flex items-center justify-between'>
                                            <div className='text-[12px]/[16px]'>
                                                Request Notes
                                            </div>
                                            <div className='flex items-center gap-2'>
                                                <div className='text-[12px]/[16px] font-bold'>
                                                    {requestNote.created_by}
                                                </div>
                                                <button
                                                    onClick={() => setEditRequestNotesModal(true)}
                                                    title='Edit request notes'
                                                >
                                                    <FiEdit2 className='text-primary' />
                                                </button>
                                            </div>
                                        </div>
                                        <div className='bg-[#F4F4F5] p-2 text-[14px]/[20px]'>
                                            {requestNote.note}
                                        </div>
                                        <div className='text-right text-[12px]/[16px] text-[#949599]'>
                                            {`Submitted on ${formatDateIfValid(
                                                requestNote.created_date,
                                                'MM/dd/yy'
                                            )}`}
                                        </div>
                                    </div>
                                );
                            } else if (item.type === 'approval') {
                                const approvalData = item.data as {
                                    approved_date?: string | null;
                                    request_approved_date?: string | null;
                                    ps_approved_date?: string | null;
                                    finance_approved_date?: string | null;
                                    ps_approver_person?: {
                                        first_name: string;
                                        last_name: string;
                                    } | null;
                                    finance_approver_person?: {
                                        first_name: string;
                                        last_name: string;
                                    } | null;
                                    comments?: string;
                                };

                                // Build approval details
                                const approvalDetails: string[] = [];

                                if (
                                    approvalData.ps_approved_date &&
                                    approvalData.ps_approver_person
                                ) {
                                    const psName = `${approvalData.ps_approver_person.first_name} ${approvalData.ps_approver_person.last_name}`;
                                    approvalDetails.push(
                                        `Patient Services: ${psName} on ${formatDateIfValid(
                                            approvalData.ps_approved_date,
                                            'MM/dd/yy'
                                        )}`
                                    );
                                }

                                if (
                                    approvalData.finance_approved_date &&
                                    approvalData.finance_approver_person
                                ) {
                                    const financeName = `${approvalData.finance_approver_person.first_name} ${approvalData.finance_approver_person.last_name}`;
                                    approvalDetails.push(
                                        `Finance: ${financeName} on ${formatDateIfValid(
                                            approvalData.finance_approved_date,
                                            'MM/dd/yy'
                                        )}`
                                    );
                                }

                                return (
                                    <div key={item.id}>
                                        <div className='flex justify-between'>
                                            <div className='text-[12px]/[16px]'>
                                                {formatDateIfValid(
                                                    approvalData.ps_approved_date ||
                                                        approvalData.finance_approved_date ||
                                                        approvalData.request_approved_date ||
                                                        approvalData.approved_date ||
                                                        '',
                                                    'MM/dd/yy'
                                                )}
                                            </div>
                                            <div className='text-green-600 text-[12px]/[16px] font-bold'>
                                                Campaign Approved
                                            </div>
                                        </div>
                                        <div className='border-green-200 border bg-[#F0FDF4] p-2 text-[14px]/[20px]'>
                                            {approvalDetails.length > 0 ? (
                                                <div className='space-y-1'>
                                                    {approvalDetails.map(
                                                        (detail, idx) => (
                                                            <div key={idx}>
                                                                {detail}
                                                            </div>
                                                        )
                                                    )}
                                                    {approvalData.comments && (
                                                        <div className='border-green-200 mt-2 border-t pt-2 text-sm'>
                                                            <strong>
                                                                Comments:
                                                            </strong>{' '}
                                                            {
                                                                approvalData.comments
                                                            }
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div>
                                                    Application was approved
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            } else {
                                const note = item.data as INote;
                                const hasNotes = note.note.length > 0;
                                const handleNotesClick = () => {
                                    if (hasNotes) {
                                        setNotesModal({
                                            isOpen: true,
                                            note: note,
                                        });
                                    }
                                };

                                return (
                                    <div key={item.id}>
                                        <div className='flex items-center justify-between'>
                                            <div className='flex items-center gap-1 text-[12px]/[16px]'>
                                                {note.todo_flag && (
                                                    note.completed_date
                                                        ? <Check />
                                                        : <FaUser className='h-4 w-4' style={{ color: '#f97316' }} />
                                                )}
                                                {formatDateIfValid(
                                                    note.created_date,
                                                    'MM/dd/yy'
                                                )}
                                                {' '}- {note.todo_flag ? 'To Do' : 'Note'}
                                            </div>
                                            <div className='flex items-center gap-2'>
                                                <div className='text-[12px]/[16px] font-bold'>
                                                    {note.created_by_name}
                                                </div>
                                                <button onClick={handleNotesClick} title='View note'>
                                                    <FiEye className='text-primary' />
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        setEditModal({
                                                            isOpen: true,
                                                            note: note,
                                                        })
                                                    }
                                                    title='Edit note'
                                                >
                                                    <FiEdit2 className='text-primary' />
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            className='cursor-pointer bg-[#F4F4F5] p-2 text-[14px]/[20px] transition-colors hover:text-primary [&_a]:text-primary [&_a]:underline'
                                            onClick={handleNotesClick}
                                            title={
                                                hasNotes
                                                    ? 'Click to view full notes'
                                                    : ''
                                            }
                                        >
                                            {safeParseHtml(
                                                clip(note.note, 140, {
                                                    html: true,
                                                    maxLines: 5,
                                                })
                                            )}
                                        </div>
                                        {note.assignee && (
                                            <div className='mt-1 text-[12px]/[16px] text-[#949599]'>
                                                Assigned to: {note.assignee}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                        })}
                        </div>
                    )}

                        <NotesModal
                            isOpen={notesModal.isOpen}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            note={notesModal.note as any}
                            created_by_name={notesModal.note?.created_by_name}
                            created_date={notesModal.note?.created_date}
                            campaignId={
                                selectedNoteIsCRNote ? undefined : campaignId
                            }
                            onClose={() =>
                                setNotesModal({ isOpen: false, note: null })
                            }
                            onPinToggle={selectedNoteIsCRNote ? undefined : (updatedNote) => {
                                setNotes((prevNotes) =>
                                    prevNotes.map((n) =>
                                        n.id === updatedNote.id
                                            ? {
                                                  ...n,
                                                  pinned_flag:
                                                      updatedNote.pinned_flag ??
                                                      false,
                                              }
                                            : n
                                    )
                                );
                            }}
                            onDelete={(noteId) => {
                                setNotes((prevNotes) =>
                                    prevNotes.filter((n) => n.id !== noteId)
                                );
                            }}
                            customDeleteFn={
                                selectedNoteIsCRNote && campaignRequestId
                                    ? (noteId) =>
                                          deleteCampaignRequestNote(
                                              campaignRequestId,
                                              noteId
                                          )
                                    : undefined
                            }
                            customFetchHistoryFn={
                                selectedNoteIsCRNote && campaignRequestId
                                    ? (noteId) =>
                                          fetchCampaignRequestNoteEditHistory(
                                              campaignRequestId,
                                              noteId
                                          )
                                    : undefined
                            }
                        />

                        <EditNoteModal
                            isOpen={editModal.isOpen}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            note={editModal.note as any}
                            campaignId={editNoteIsCRNote ? 0 : campaignId || 0}
                            onClose={() =>
                                setEditModal({ isOpen: false, note: null })
                            }
                            onSave={(updatedNote) => {
                                setNotes((prevNotes) =>
                                    prevNotes.map((n) =>
                                        n.id === updatedNote.id
                                            ? {
                                                  ...n,
                                                  note: updatedNote.note,
                                                  todo_flag: updatedNote.todo_flag ?? n.todo_flag,
                                                  completed_date: updatedNote.completed_date ?? null,
                                                  completed_by_name: updatedNote.completed_by_name || '',
                                                  assignee: updatedNote.assignee,
                                                  assignment: updatedNote.assignment ? Number(updatedNote.assignment) : null,
                                              } as INote
                                            : n
                                    )
                                );
                            }}
                            hidePinToggle={editNoteIsCRNote}
                            customUpdateFn={
                                editNoteIsCRNote && campaignRequestId
                                    ? (noteId, payload) =>
                                          updateCampaignRequestNote(
                                              campaignRequestId,
                                              noteId,
                                              payload
                                          )
                                    : undefined
                            }
                            customFetchHistoryFn={
                                editNoteIsCRNote && campaignRequestId
                                    ? (noteId) =>
                                          fetchCampaignRequestNoteEditHistory(
                                              campaignRequestId,
                                              noteId
                                          )
                                    : undefined
                            }
                        />

                        <EditContactAttemptModal
                            isOpen={editAttemptModal.isOpen}
                            attempt={editAttemptModal.attempt}
                            onClose={() => setEditAttemptModal({ isOpen: false, attempt: null })}
                            onSave={(updated) => {
                                setContactAttempts((prev) =>
                                    prev.map((a) => (a.id === updated.id ? updated : a))
                                );
                            }}
                        />

                        {campaignRequest?.id && (
                            <EditRequestNotesModal
                                isOpen={editRequestNotesModal}
                                notes={campaignRequestNotes || ''}
                                campaignRequestId={campaignRequest.id}
                                onClose={() => setEditRequestNotesModal(false)}
                                onSave={(updatedNotes) => {
                                    onRequestNotesUpdate?.(updatedNotes);
                                }}
                            />
                        )}
                    </>
                )}

                {/* Activity Log Tab Content */}
                {activeTab === 'activity' && campaignId && (
                    <>
                        <h3 className='mb-3 text-[16px]/[22px] font-bold'>
                            Activity Log ({activityLogCount})
                        </h3>
                        <ActivityLog campaignId={campaignId} refreshKey={activityLogRefreshKey} />
                    </>
                )}
            </div>
        </Panel>
    );
};

export default ApplicationHistory;
